import React, { useState, useEffect } from 'react';
import { getVendors, syncVendors } from '../../adminApi';
import { Vendor, SyncResult } from '../../adminTypes';
import VendorDetailPanel from './VendorDetailPanel';

const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  US_FOODS: 'Special Processing - US Foods',
  GFS: 'Special Processing - GFS',
  STAPLES: 'Special Processing - Staples',
  OTHER: 'Standard',
};
const getWorkflowTypeLabel = (type: string) => WORKFLOW_TYPE_LABELS[type] || type;

const VendorManagement: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<SyncResult | null>(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    const q = searchQuery.toLowerCase();
    setFilteredVendors(
      vendors.filter(v =>
        String(v.vendor_number).includes(q) ||
        v.vendor_name.toLowerCase().includes(q)
      )
    );
  }, [searchQuery, vendors]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const data = await getVendors();
      setVendors(data);
    } catch {
      alert('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncVendors();
      setSyncProgress(result);
      setShowSyncModal(true);
      await fetchVendors();
    } catch {
      alert('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleVendorSaved = (updated: Vendor) => {
    setVendors(prev => prev.map(v => v.vendor_number === updated.vendor_number ? updated : v));
    setSelectedVendor(updated);
  };

  const getPaymentMethodName = (id: number): string => {
    const m: Record<number, string> = { 1: 'Check', 2: 'Credit Card', 3: 'ACH', 4: 'EFT' };
    return m[id] || 'Unknown';
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading vendors...</div>;
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ color: '#00386C', margin: 0 }}>Vendor Management</h1>
        <p style={{ color: '#666', fontSize: '13px', margin: '6px 0 0 0' }}>
          Click any vendor row to view and edit details, GL keywords, and parts.
        </p>
      </div>

      {/* Sync */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{
            padding: '10px 16px',
            backgroundColor: '#00386C',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: syncing ? 'not-allowed' : 'pointer',
            opacity: syncing ? 0.6 : 1,
            fontSize: '14px',
            fontWeight: 'bold',
          }}
        >
          {syncing ? 'Syncing...' : 'Sync from WinTeam'}
        </button>
        {syncing && <span style={{ color: '#666' }}>Synchronizing vendors...</span>}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search by vendor name or number..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            width: '100%', maxWidth: '400px',
            padding: '10px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '14px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <thead>
            <tr style={{ backgroundColor: '#00386C', color: 'white' }}>
              <th style={{ padding: '12px', textAlign: 'left' }}>Vendor #</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Workflow Type</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Payment Method</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Active</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Last Synced</th>
            </tr>
          </thead>
          <tbody>
            {filteredVendors.map((vendor, idx) => (
              <tr
                key={vendor.vendor_number}
                onClick={() => setSelectedVendor(vendor)}
                style={{
                  backgroundColor: idx % 2 === 0 ? '#f9f9f9' : 'white',
                  borderBottom: '1px solid #eee',
                  cursor: 'pointer',
                  transition: 'background-color 0.12s',
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#e8f0f7')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#f9f9f9' : 'white')}
              >
                <td style={{ padding: '12px', fontWeight: 'bold', color: '#00386C' }}>{vendor.vendor_number}</td>
                <td style={{ padding: '12px' }}>{vendor.vendor_name}</td>
                <td style={{ padding: '12px' }}>
                  <span style={{ display: 'inline-block', padding: '3px 8px', backgroundColor: '#e8f0f7', borderRadius: '3px', fontSize: '12px' }}>
                    {getWorkflowTypeLabel(vendor.workflow_type || 'OTHER')}
                  </span>
                </td>
                <td style={{ padding: '12px' }}>{getPaymentMethodName(vendor.default_payment_method_id || 1)}</td>
                <td style={{ padding: '12px' }}>
                  <span style={{
                    display: 'inline-block', width: '22px', height: '22px', borderRadius: '11px',
                    backgroundColor: vendor.active !== false ? '#4CAF50' : '#ccc',
                    color: 'white', textAlign: 'center', lineHeight: '22px', fontSize: '12px', fontWeight: 'bold',
                  }}>
                    {vendor.active !== false ? '✓' : '✗'}
                  </span>
                </td>
                <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                  {vendor.winteam_last_synced_at ? new Date(vendor.winteam_last_synced_at).toLocaleDateString() : 'Never'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredVendors.length === 0 && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '4px', marginTop: '20px' }}>
          No vendors found matching your search.
        </div>
      )}

      {/* Vendor Detail Slide-Over Panel */}
      {selectedVendor && (
        <VendorDetailPanel
          key={selectedVendor.vendor_number}
          vendor={selectedVendor}
          onClose={() => setSelectedVendor(null)}
          onVendorSaved={handleVendorSaved}
        />
      )}

      {/* Sync Result Modal */}
      {showSyncModal && syncProgress && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '6px', padding: '24px', maxWidth: '400px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
            <h2 style={{ color: '#00386C', marginTop: 0 }}>Sync Results</h2>
            <div style={{ marginBottom: '12px', fontSize: '14px' }}>
              <p style={{ margin: '8px 0' }}><strong>Inserted:</strong> {syncProgress.inserted}</p>
              <p style={{ margin: '8px 0' }}><strong>Updated:</strong> {syncProgress.updated}</p>
              {syncProgress.errors && syncProgress.errors.length > 0 && (
                <div style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '3px', padding: '10px', marginTop: '10px' }}>
                  <p style={{ color: '#721c24', fontWeight: 'bold', margin: '0 0 8px 0' }}>Errors:</p>
                  {syncProgress.errors.map((err, idx) => (
                    <p key={idx} style={{ color: '#721c24', margin: '4px 0', fontSize: '12px' }}>
                      {err.vendor_number ? `Vendor ${err.vendor_number}: ` : ''}{err.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowSyncModal(false)}
              style={{ width: '100%', padding: '10px', backgroundColor: '#00386C', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;
